import { UserListItem } from '../interfaces/user.interface';
import { PersonTableRow } from '../interfaces/person-table.interface';
import { formatNombreCompletoDisplay } from './nombre-texto.helper';

const REUNION_LABELS: Record<number, string> = {
  [-1]: 'No asistirá',
  0: 'Sin definir',
  1: '1er Reunion',
  2: '2da Reunion',
  3: '3ra Reunion',
  4: '4ta Reunion',
  5: '5ta Reunion',
  6: '6ta Reunion',
  7: '7ma Reunion',
  8: '8va Reunion',
  100: 'Multiple reuniones',
};

/** Parsea YYYY-MM-DD como fecha local (evita desfase UTC). */
function parseNacimientoLocal(nacimiento: string | Date): Date | null {
  if (nacimiento instanceof Date) {
    return Number.isNaN(nacimiento.getTime()) ? null : new Date(nacimiento.getTime());
  }
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(nacimiento.trim());
  if (isoDay) {
    return new Date(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3]));
  }
  const parsed = new Date(nacimiento);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Dias hasta el proximo cumpleanos; null si no hay nacimiento. */
export function daysUntilBirthday(nacimiento?: string | null, hoy: Date = new Date()): number | null {
  if (!nacimiento) {
    return null;
  }

  const birth = parseNacimientoLocal(nacimiento);
  if (!birth) {
    return null;
  }

  const today = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const next = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  if (next < today) {
    next.setFullYear(today.getFullYear() + 1);
  }

  return Math.round((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatCumpleEn(nacimiento?: string | null): string {
  const days = daysUntilBirthday(nacimiento);
  if (days === null) {
    return '\u2014';
  }
  if (days === 0) {
    return 'Hoy';
  }
  if (days === 1) {
    return 'Ma\u00f1ana';
  }
  return `${days} d\u00edas`;
}

export function formatMotivo(
  user: Pick<UserListItem, 'estudiando' | 'inscBautismo' | 'soliEstudio' | 'soliBautismo'>,
): string {
  const estudio = user.estudiando === true || user.soliEstudio === true;
  const bautismo = user.inscBautismo === true || user.soliBautismo === true;
  if (estudio && bautismo) {
    return 'Est. y Bautismo';
  }
  if (estudio) {
    return user.estudiando ? 'Estudiando' : 'Solicitud estudio';
  }
  if (bautismo) {
    return user.inscBautismo ? 'Bautismo' : 'Solicitud bautismo';
  }
  return '\u2014';
}

export function formatReunion(reunionSelect?: number | null): string {
  if (reunionSelect === null || reunionSelect === undefined) {
    return '—';
  }
  return REUNION_LABELS[reunionSelect] ?? '—';
}

/** Iniciales nombre + apellido (2 letras). */
export function buildIniciales(nombre?: string, apellido?: string): string {
  const n = (nombre ?? '').trim().charAt(0);
  const a = (apellido ?? '').trim().charAt(0);
  return (n + a).toUpperCase() || '?';
}

/** Celeste (primary) hombre, rosa mujer. */
export function buildAvatarStyle(sexo?: string | null): Record<string, string> {
  const mujer = (sexo ?? '').toLowerCase() === 'mujer' || (sexo ?? '').toLowerCase() === 'femenino';
  const bg = mujer ? '#f9a8d4' : 'var(--p-primary-color)';
  const color = mujer ? '#831843' : 'var(--p-primary-contrast-color, #fff)';
  return {
    backgroundColor: bg,
    color,
    width: 'calc(1.75rem - 2px)',
    height: 'calc(1.75rem - 2px)',
    fontSize: '0.7rem',
    fontWeight: '600',
  };
}

export function buildWhatsappUrl(prefijo?: string | null, celular?: string | number | null, nombre?: string): string | null {
  const digits = `${prefijo ?? ''}${celular ?? ''}`.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const text = encodeURIComponent(`Hola ${nombre ?? ''}, `);
  return `https://wa.me/${digits}?text=${text}`;
}

export function mapUserToPersonTableRow(user: UserListItem): PersonTableRow {
  const nombreCompleto = formatNombreCompletoDisplay(user.nombre, user.nombre2, user.apellido);
  const prefijo = user.prefijo ?? '';
  const celular = user.celular != null ? String(user.celular) : '';
  const faltas = user.faltas ?? 0;

  return {
    id: user.id,
    nombreCompleto,
    iniciales: buildIniciales(user.nombre, user.apellido),
    avatarStyle: buildAvatarStyle(user.sexo),
    esLider: user.lider === true,
    esAdmin: user.admin === true,
    esContador: user.contador === true,
    nacimiento: user.nacimiento ?? null,
    edadLabel: '—',
    miLider: user.miLider || '—',
    celular,
    prefijo,
    whatsappUrl: buildWhatsappUrl(prefijo, celular, formatNombreCompletoDisplay(user.nombre)),
    ciudad: user.ciudad || '—',
    faltasLabel: `${faltas} faltas`,
    estadoLabel: 'Sin informar',
    cumpleEnLabel: formatCumpleEn(user.nacimiento),
    motivoLabel: formatMotivo(user),
    reunionLabel: formatReunion(user.reunionSelect),
    faltas,
    contactadoId: user.contactadoId ?? 'nadie',
    estudiando: user.estudiando === true,
    inscBautismo: user.inscBautismo === true,
    idLider: user.idLider,
    nombre: user.nombre,
    apellido: user.apellido,
    nivelPersonal: user.nivelPersonal,
  };
}